import { useCallback } from 'react';
import noop from 'lodash/noop';
import { useFlagsService } from '@atlassian/jira-flags';
import { defineMessage } from '@atlassian/jira-intl';

const changeToOldEditorTitle = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.change-to-old-editor-title',
	defaultMessage: "You've changed the default editor",
	description:
		'Title for success flag when user successfully updated their editor preference to old editor',
});

const changeToOldEditorDescription = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.change-to-old-editor-description',
	defaultMessage: 'You can use the old editor as needed while we continue building the new editor.',
	description:
		'Description for success flag when user successfully updated their editor preference to old editor',
});

const learnMore = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.learn-more',
	defaultMessage: 'Learn more',
	description: 'Label for learn more link in success flag',
});

const switchToNewEditor = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.switch-to-new-editor',
	defaultMessage: 'Switch to new editor',
	description: 'Label for switch to new editor link in success flag',
});

const changeToNewEditorTitle = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.change-to-new-editor-title',
	defaultMessage: 'Welcome to the new workflow editor',
	description:
		'Title for success flag when user successfully updated their editor preference to new editor',
});

const changeToNewEditorDescription = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.change-to-new-editor-description',
	defaultMessage:
		'Enjoy a faster, cleaner, more intuitive experience while creating and editing your workflows.',
	description:
		'Description for success flag when user successfully updated their editor preference to new editor',
});

const ok = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.ok',
	defaultMessage: 'Ok',
	description: 'Label for ok button in success flag',
});

const updatePreferenceErrorFlagTitle = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.update-preference-error-flag-title',
	defaultMessage: "We couldn't update your default editor",
	description:
		"Title for error flag when there was a failure updating the user's editor preference",
});

const updatePreferenceErrorFlagDescription = defineMessage({
	id: 'workflow-editor-preference-settings.controllers.change-preference-flag.update-preference-error-flag-description',
	defaultMessage: 'Select Confirm to try again.',
	description:
		"Description for error flag when there was a failure updating the user's editor preference",
});

const NEW_EDITOR_SUCCESS_FLAG = 'new-editor-success-flag';

const learnMoreLinkNewEditor =
	'https://support.atlassian.com/jira-service-management-cloud/docs/using-the-new-workflow-editor/';
const learnMoreLinkOldEditor =
	'https://support.atlassian.com/jira-service-management-cloud/docs/changing-your-default-workflow-editor/';

export const useChangePreferenceFlag = () => {
	const { showFlag, dismissFlag } = useFlagsService();
	const onClickLearnMoreOldEditor = () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.open(learnMoreLinkOldEditor, '_blank');
	};

	const onClickLearnMoreNewEditor = () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.open(learnMoreLinkNewEditor, '_blank');
	};

	const showNewEditorSuccessFlag = useCallback(
		() =>
			showFlag({
				id: NEW_EDITOR_SUCCESS_FLAG,
				testId: 'workflow-editor-preference-settings.ui.success-flag',
				type: 'success',
				title: changeToNewEditorTitle,
				description: changeToNewEditorDescription,
				messageId:
					'workflow-editor-preference-settings.controllers.change-preference-flag.show-flag.success.change-to-new-editor',
				messageType: 'transactional',
				actions: [
					{
						content: learnMore,
						onClick: onClickLearnMoreNewEditor,
					},
					{
						content: ok,
						onClick: () => dismissFlag(NEW_EDITOR_SUCCESS_FLAG),
					},
				],
			}),
		[dismissFlag, showFlag],
	);

	const showOldEditorSuccessFlag = useCallback(
		(openDefaultEditorDialog: () => void = noop) =>
			showFlag({
				type: 'success',
				title: changeToOldEditorTitle,
				description: changeToOldEditorDescription,
				messageId:
					'workflow-editor-preference-settings.controllers.change-preference-flag.show-flag.success.change-to-old-editor',
				messageType: 'transactional',
				actions: [
					{
						content: learnMore,
						onClick: onClickLearnMoreOldEditor,
					},
					{
						content: switchToNewEditor,
						onClick: openDefaultEditorDialog,
					},
				],
			}),
		[showFlag],
	);

	const showErrorFlag = useCallback(
		() =>
			showFlag({
				type: 'error',
				title: updatePreferenceErrorFlagTitle,
				description: updatePreferenceErrorFlagDescription,
				messageId:
					'workflow-editor-preference-settings.controllers.change-preference-flag.show-flag.error.update-preference-error',
				messageType: 'transactional',
			}),
		[showFlag],
	);

	return { showNewEditorSuccessFlag, showOldEditorSuccessFlag, showErrorFlag };
};
